import { FileText, PanelLeft, Settings } from "lucide-react";

export type RailPane = "files" | "settings" | null;

interface DeskRailProps {
  active: RailPane;
  onToggle: (pane: RailPane) => void;
}

/**
 * Thin icon rail on the far left edge. Like VS Code's activity bar.
 * Clicking an active button collapses its panel, giving max space to sessions.
 */
export function DeskRail({ active, onToggle }: DeskRailProps) {
  return (
    <div
      className="hidden md:flex w-10 flex-col items-center py-2 gap-1 border-r"
      style={{
        background: "var(--surface)",
        borderColor: "var(--blt)",
        flexShrink: 0,
      }}
    >
      <RailButton
        icon={<FileText size={14} />}
        label="Files"
        active={active === "files"}
        onClick={() => onToggle(active === "files" ? null : "files")}
      />
      <RailButton
        icon={<Settings size={14} />}
        label="Settings"
        active={active === "settings"}
        onClick={() => onToggle(active === "settings" ? null : "settings")}
      />
      <div className="flex-1" />
      <RailButton
        icon={<PanelLeft size={14} />}
        label={active === null ? "Open files" : "Close panel"}
        active={false}
        onClick={() => onToggle(active === null ? "files" : null)}
      />
    </div>
  );
}

function RailButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="w-8 h-8 rounded flex items-center justify-center transition-colors relative group"
      style={{
        background: active
          ? "color-mix(in srgb, var(--accent) 12%, transparent)"
          : "transparent",
        color: active ? "var(--accent)" : "var(--t3)",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          (e.currentTarget as HTMLElement).style.background = "var(--hover)";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          (e.currentTarget as HTMLElement).style.background = "transparent";
        }
      }}
    >
      {icon}
      {active && (
        <span
          className="absolute left-0 top-1 bottom-1 w-[2px] rounded-r"
          style={{ background: "var(--accent)" }}
        />
      )}
    </button>
  );
}
